import React, {useMemo} from 'react';
import {RootColoredText} from './RootColoredText';
import {parseRootLetters} from '../helpers/rootLetterMnemonics';
import {masteryBucket} from '../helpers/verbProgress';

const normalizeKey = (value = '') =>
  value
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeTense = (tense = '') => {
  const key = normalizeKey(tense);
  if (key.includes('past') || key.includes('עבר')) return 'Past';
  if (key.includes('present') || key.includes('הווה')) return 'Present';
  if (key.includes('future') || key.includes('עתיד')) return 'Future';
  if (key.includes('imperative') || key.includes('ציווי')) return 'Imperative';
  return '';
};

const normalizeVoice = (form = {}) => {
  const voice = normalizeKey(form.voice || '');
  if (voice.includes('passive') || voice.includes('pual') || voice.includes('hufal')) return 'passive';
  return 'active';
};

const findFormByRule = (tenseForms, check) =>
  tenseForms.find((form) => {
    const p = String(form.person || '').trim();
    const ng = String(form.number_gender || form.numberGender || '').toLowerCase().trim();
    return check(p, ng);
  });

const renderTransliteration = (text, stressIndex) => {
  if (!text) return null;
  if (stressIndex == null || stressIndex < 0 || stressIndex >= text.length) {
    return <span>{text}</span>;
  }
  const before = text.slice(0, stressIndex);
  const stressed = text[stressIndex];
  const after = text.slice(stressIndex + 1);
  return (
    <>
      {before}
      <span className="conjugation-translit-stress">{stressed}</span>
      {after}
    </>
  );
};

const ConjugationEntry = ({entry, rootLetters}) => {
  if (!entry) {
    return <div className="conjugation-entry empty" />;
  }
  const bucket = masteryBucket(entry.masteryLevel);
  return (
    <div className="conjugation-entry">
      <span
        className={`mastery-dot mastery-${bucket.key}`}
        title={`${bucket.label} (${entry.masteryLevel}/5)`}
        aria-label={`${bucket.label}`}
      />
      <div className="conjugation-pronouns font-hebrew">{entry.pronouns}</div>
      <div className="conjugation-form font-hebrew">
        <RootColoredText text={entry.hebrew} rootLetters={rootLetters} />
      </div>
      <div className="conjugation-translit">
        {renderTransliteration(entry.transliteration, entry.stressIndex)}
      </div>
    </div>
  );
};

const ConjugationSection = ({title, rows, rootLetters}) => (
  <div className="conjugation-section-card">
    <div className="conjugation-section-header">
      <div className="conjugation-section-title">{title}</div>
      <div className="conjugation-section-divider" />
    </div>
    <div className="conjugation-grid">
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          <ConjugationEntry entry={row.left} rootLetters={rootLetters} />
          <ConjugationEntry entry={row.right} rootLetters={rootLetters} />
        </React.Fragment>
      ))}
    </div>
  </div>
);

const TENSE_CONFIGS = {
  Present: {
    title: 'Present tense',
    rows: [
      {
        right: {
          pronouns: 'אני/אתה/הוא',
          check: (p, ng) => ng === 'ms' || ng === 'm',
        },
        left: {
          pronouns: 'אנחנו/אתם/הם',
          check: (p, ng) => ng === 'mp',
        },
      },
      {
        right: {
          pronouns: 'אני/את/היא',
          check: (p, ng) => ng === 'fs' || ng === 'f',
        },
        left: {
          pronouns: 'אנחנו/אתן/הן',
          check: (p, ng) => ng === 'fp',
        },
      },
    ],
  },
  Past: {
    title: 'Past tense',
    rows: [
      {
        right: {pronouns: 'אני', check: (p, ng) => p === '1' && (ng === 'sg' || ng === 's' || ng === 'ms' || ng === 'fs')},
        left: {pronouns: 'אנחנו', check: (p, ng) => p === '1' && (ng === 'pl' || ng === 'p' || ng === 'mp' || ng === 'fp')},
      },
      {
        right: {pronouns: 'אתה', check: (p, ng) => p === '2' && (ng === 'ms' || ng === 'm')},
        left: {pronouns: 'אתם', check: (p, ng) => p === '2' && ng === 'mp'},
      },
      {
        right: {pronouns: 'את', check: (p, ng) => p === '2' && (ng === 'fs' || ng === 'f')},
        left: {pronouns: 'אתן', check: (p, ng) => p === '2' && ng === 'fp'},
      },
      {
        right: {pronouns: 'הוא', check: (p, ng) => p === '3' && (ng === 'ms' || ng === 'm' || ng === 'sg' || ng === 's')},
        left: {pronouns: 'הם', check: (p, ng) => p === '3' && (ng === 'mp' || ng === 'pl' || ng === 'p')},
      },
      {
        right: {pronouns: 'היא', check: (p, ng) => p === '3' && (ng === 'fs' || ng === 'f')},
        left: {pronouns: 'הן', check: (p, ng) => p === '3' && (ng === 'fp' || ng === 'pl' || ng === 'p')},
      },
    ],
  },
  Future: {
    title: 'Future tense',
    rows: [
      {
        right: {pronouns: 'אני', check: (p, ng) => p === '1' && (ng === 'sg' || ng === 's' || ng === 'ms' || ng === 'fs')},
        left: {pronouns: 'אנחנו', check: (p, ng) => p === '1' && (ng === 'pl' || ng === 'p' || ng === 'mp' || ng === 'fp')},
      },
      {
        right: {pronouns: 'אתה', check: (p, ng) => p === '2' && (ng === 'ms' || ng === 'm')},
        left: {pronouns: 'אתם', check: (p, ng) => p === '2' && ng === 'mp'},
      },
      {
        right: {pronouns: 'את', check: (p, ng) => p === '2' && (ng === 'fs' || ng === 'f')},
        left: {pronouns: 'אתן', check: (p, ng) => p === '2' && ng === 'fp'},
      },
      {
        right: {pronouns: 'הוא', check: (p, ng) => p === '3' && (ng === 'ms' || ng === 'm' || ng === 'sg' || ng === 's')},
        left: {pronouns: 'הם', check: (p, ng) => p === '3' && (ng === 'mp' || ng === 'pl' || ng === 'p')},
      },
      {
        right: {pronouns: 'היא', check: (p, ng) => p === '3' && (ng === 'fs' || ng === 'f')},
        left: {pronouns: 'הן', check: (p, ng) => p === '3' && (ng === 'fp' || ng === 'pl' || ng === 'p')},
      },
    ],
  },
  Imperative: {
    title: 'Imperative',
    rows: [
      {
        right: {pronouns: 'אתה', check: (p, ng) => ng === 'ms' || ng === 'm'},
        left: {pronouns: 'אתם', check: (p, ng) => ng === 'mp'},
      },
      {
        right: {pronouns: 'את', check: (p, ng) => ng === 'fs' || ng === 'f'},
        left: {pronouns: 'אתן', check: (p, ng) => ng === 'fp'},
      },
    ],
  },
};

const TENSE_ORDER = ['Present', 'Past', 'Future', 'Imperative'];

const buildSectionData = (tense, tenseForms, rootLetters, progressMap) => {
  const config = TENSE_CONFIGS[tense];
  if (!config) return null;

  const masteryFor = (formId) =>
    progressMap && formId ? Number(progressMap[formId]?.masteryLevel) || 0 : 0;

  const rows = config.rows
    .map((row) => {
      const rightForm = findFormByRule(tenseForms, row.right.check);
      const leftForm = findFormByRule(tenseForms, row.left.check);

      if (!rightForm && !leftForm) return null;

      return {
        right: rightForm
          ? {
              pronouns: row.right.pronouns,
              formId: rightForm.id,
              hebrew: rightForm.form_he,
              transliteration: rightForm.transliteration,
              stressIndex: rightForm.stressIndex,
              masteryLevel: masteryFor(rightForm.id),
            }
          : null,
        left: leftForm
          ? {
              pronouns: row.left.pronouns,
              formId: leftForm.id,
              hebrew: leftForm.form_he,
              transliteration: leftForm.transliteration,
              stressIndex: leftForm.stressIndex,
              masteryLevel: masteryFor(leftForm.id),
            }
          : null,
      };
    })
    .filter(Boolean);

  if (rows.length === 0) return null;

  return {title: config.title, rows};
};

export const VerbConjugationTable = ({forms = [], root = '', progressMap = null}) => {
  const rootLetters = useMemo(() => parseRootLetters(root), [root]);

  const voices = useMemo(() => {
    const groups = {active: [], passive: []};
    forms.forEach((form) => {
      const voice = normalizeVoice(form);
      groups[voice].push(form);
    });
    return groups;
  }, [forms]);

  const hasAnyVoice = voices.active.length > 0 || voices.passive.length > 0;
  if (!hasAnyVoice) {
    return <div className="conjugation-no-data">No conjugations available.</div>;
  }

  const renderSections = (voiceForms) => {
    const sections = TENSE_ORDER.map((tense) => {
      const tenseForms = voiceForms.filter((f) => normalizeTense(f.tense) === tense);
      return buildSectionData(tense, tenseForms, rootLetters, progressMap);
    }).filter(Boolean);

    if (sections.length === 0) {
      return <div className="conjugation-no-data">No conjugations available.</div>;
    }

    return sections.map((section, i) => (
      <ConjugationSection key={i} title={section.title} rows={section.rows} rootLetters={rootLetters} />
    ));
  };

  return (
    <div className="verb-conjugation-sections">
      {voices.active.length > 0 && renderSections(voices.active)}
      {voices.passive.length > 0 && (
        <>
          <div className="conjugation-section-title" style={{marginTop: 16}}>Passive Voice</div>
          {renderSections(voices.passive)}
        </>
      )}
    </div>
  );
};
